import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useProfileQuery } from "@/queries/user/profile";

import { QueryKeys } from "@/queries/utils";
import { Candidate } from "@/queries/candidate/candidate";
import { IReference } from "@/redux/features/candidateProfileSlice";
import { toast } from "sonner";

export const useDeleteReference = (
  profileId: string,
  referenceId: string,
  onSuccess?: () => void,
) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();
  const { profile } = useProfileQuery();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();

      return request<IReference>({
        method: "DELETE",
        path: `/api/candidate/${profileId || "foo"}/reference/${referenceId ?? ""}`,
        token,
      });
    },
    onSuccess: () => {
      const prevCandidate = qc.getQueryData<Candidate>(
        QueryKeys.candidate(profileId),
      );

      if (prevCandidate) {
        const updatedCandidate = {
          ...prevCandidate,
        };

        const references = [...prevCandidate.references];
        const prevReferenceIdx = references.findIndex(
          ({ id }) => id === referenceId,
        );

        if (prevReferenceIdx !== -1) {
          references.splice(prevReferenceIdx, 1);
        }
        updatedCandidate.references = references;

        qc.setQueryData<Candidate>(
          QueryKeys.candidate(profileId),
          updatedCandidate,
        );
      }

      if (profile?.id === profileId) {
        const prevSelf = qc.getQueryData<Candidate>(QueryKeys.myCandidate);
        if (prevSelf) {
          const updatedCandidate = {
            ...prevSelf,
          };

          const references = [...prevSelf.references];
          const prevReferenceIdx = references.findIndex(
            ({ id }) => id === referenceId,
          );

          if (prevReferenceIdx !== -1) {
            references.splice(prevReferenceIdx, 1);
          }
          updatedCandidate.references = references;

          qc.setQueryData<Candidate>(QueryKeys.myCandidate, updatedCandidate);
        }
      }

      onSuccess?.();
    },
    onError: () => {
      toast.error("Failed to delete reference, please try again later.");
    },
  });

  return {
    deleteReference: mutate,
    isDeletingReference: isPending,
    deleteReferenceError: error,
  };
};
